import { type InputHTMLAttributes, type JSX, useId } from 'react';

import { cn } from '../lib/cn';

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string;
  hint?: string;
  error?: string;
}

/**
 * Same shape as `Field`/`Checkbox` applied to a radio input. Exclusive
 * selection within a group comes from the native `name` attribute \u2014 no
 * group state lives in this component.
 */
export function Radio({
  label,
  hint,
  error,
  className,
  required,
  ...props
}: RadioProps): JSX.Element {
  const inputId = useId();
  const messageId = `${inputId}-message`;
  const message = error ?? hint;
  const hasError = error !== undefined;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <input
          id={inputId}
          type="radio"
          required={required}
          aria-invalid={hasError}
          aria-describedby={message === undefined ? undefined : messageId}
          className={cn(
            'size-5 border-slate-300 accent-primary-600',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
            hasError
              ? 'border-red-500 focus-visible:ring-red-500'
              : 'focus-visible:ring-primary-500',
            className,
          )}
          {...props}
        />
        <label htmlFor={inputId} className="text-sm font-medium text-slate-900">
          {label}
          {required === true ? (
            <span aria-hidden="true" className="ml-0.5 text-red-600">
              *
            </span>
          ) : null}
        </label>
      </div>

      {message === undefined ? null : (
        <p
          id={messageId}
          role={hasError ? 'alert' : undefined}
          className={cn('text-xs', hasError ? 'text-red-600' : 'text-slate-500')}
        >
          {message}
        </p>
      )}
    </div>
  );
}
